<template>
  <div id="LearnRenderer" class="h-screen w-full">

    <!--- TOP PROGRESS BAR START --->
    <div class="absolute w-full z-10">
      <div class="p-8 sm:p-10 md:p-12 lg:p-16">
        <div class="flex items-center justify-center">
          <feather-icon class="mr-2" icon="XIcon" svgClasses="h-8 w-8 text-white hover:text-primary cursor-pointer"
                        @click="$router.back()"/>
          <vs-progress :percent="currentProgress" class="w-full LearnProgressBar" color="success"
                       height="14"></vs-progress>
        </div>
      </div>
    </div>
    <!--- TOP PROGRESS BAR END --->

    <!--- LEARN APP START --->
    <div class="h-screen w-full relative flex items-center justify-center">

      <div class="FcAppContainer">
        <div class="fcLeftArrow cursor-pointer-fc" @click="previousTerm">
          <feather-icon icon="ArrowLeftIcon" svgClasses="h-32 w-32"/>
        </div>

        <div class="fcCard">
          <slide-y-down-transition>
            <flashcard
              v-if="showCard"
              :key="currentTermIndex"
              :text-front="currentTerm.term"
              :text-back="currentTerm.definition">
            </flashcard>
          </slide-y-down-transition>
        </div>


        <div class="fcRightArrow cursor-pointer-fc" @click="nextTerm">
          <feather-icon icon="ArrowRightIcon" svgClasses="h-32 w-32"/>
        </div>
      </div>


      <!--- BOTTOM ACTION BAR START --->
      <div class="bottomActionBar absolute w-full z-10 bottom-0">
        <div class="p-12 pt-8 pb-8 w-full">
          <div class="flex items-center justify-center w-full">
            <div class="actionButtons flex items-center justify-between">
              <vs-button :disabled="!skipButton" class="skipButton" color="transparent"
                         type="flat" @click="">BACK
              </vs-button>
              <vs-button :disabled="!checkButton" class="nextButton" @click="checkAnswer">Add To Review Terms Set</vs-button>
            </div>
          </div>
        </div>


      </div>
      <!--- BOTTOM ACTION BAR END --->

    </div>
  </div>
</template>

<script>
import LottieAnimation from '@/components/lottie/LottieAnimation'
import Flashcard from "./components/Flashcard";

export default {
  data() {
    return {
      checkButton: false,
      finishedLesson: false,

      keyboardShortcuts: false,
      skipButton: true,

      showCard: false,
      cardAnimationActive: false,

      currentTermIndex: 0,
      terms : [
        {
          term: "AAA (authentication, authorization, and accounting)",
          definition: "A security concept where a centralized platform verifies subject identification, ensures the subject is assigned relevant permissions, and then logs these actions to create an audit trail."
        },
        {
          term: "ABAC (attribute-based access control)",
          definition: "An access control technique that evaluates a set of attributes that each subject possesses to determine if access should be granted."
        },
        {
          term: "account policies",
          definition: "A set of rules governing user security information, such as password expiration and uniqueness, which can be set globally."
        },
        {
          term: "ACL (Access Control List)",
          definition: "A collection of access control entries (ACEs) that determines which subjects (user accounts, host IP addresses, and so on) are allowed or denied access to the object and the privileges given (read only, read/write, and so on)."
        },
        {
          term: "active defense",
          definition: "The practice of responding to a threat by destroying or deceiving a threat actor's capabilities."
        },
        {
          term: "adversarial AI (adversarial artificial intelligence)",
          definition: "Using AI to identify vulnerabilities and attack vectors to circumvent security systems."
        },
        {
          term: "AES (Advanced Encryption Standard)",
          definition: "A symmetric 128-, 192-, or 256-bit block cipher based on the Rijndael algorithm developed by Belgian cryptographers Joan Daemen and Vincent Rijmen and adopted by the U.S. government as its encryption standard to replace DES."
        },
        {
          term: "Agile model (Agile)",
          definition: "A software development model that focuses on iterative and incremental development to account for evolving requirements and expectations."
        },
        {
          term: "AH (authentication header)",
          definition: "An IPSec protocol that provides authentication for the origin of transmitted data as well as integrity and protection against replay attacks."
        },
        {
          term: "air gap",
          definition: "A type of network isolation that physically separates a network from all other networks."
        },
        {
          term: "AIS (Automated Indicator Sharing)",
          definition: "Threat intelligence data feed operated by theÂ DHS."
        },
        {
          term: "ALE (annual loss expectancy)",
          definition: "The total cost of a risk to an organization on an annual basis. This is determined by multiplying the SLE by the annual rate of occurrence (ARO)."
        },
        {
          term: "AP (access point)",
          definition: "A device that provides a connection between wireless devices and can connect to wired networks. Also known as wireless access point or WAP."
        },
        {
          term: "API (application programming interface)",
          definition: "A library of programming utilities used, for example, to enable software developers to access functions of the TCP/IP network stack under a particular operating system."
        },
        {
          term: "application aware firewall",
          definition: "A Layer 7 firewall technology that inspects packets at the Application layer of the OSI model."
        },
        {
          term: "application firewall",
          definition: "Software designed to run on a server to protect a particular application such as a web server or SQL server."
        },
        {
          term: "APT (advanced persistent threat)",
          definition: "An attacker's ability to obtain, maintain, and diversify access to network systems using exploits and malware."
        },
        {
          term: "Arduino",
          definition: "Open-source platform producing programmable circuit boards for education and industrial prototyping."
        },
        {
          term: "ARO (annual rate of occurrence)",
          definition: "In risk calculation, an expression of the probability/likelihood of a risk as the number of times per year a particular loss is expected to occur."
        },
        {
          term: "ARP inspection",
          definition: "An optional security feature of a switch that prevents excessive ARP replies from flooding a network segment.L"
        },
        {
          term: "ARP poisoning (ARP spoofing)",
          definition: "A network-based attack where an attacker with access to the target local network segment redirects an IP address to the MAC address of a computer that is not the intended recipient. This can be used to perform a variety of attacks, including DoS, spoofing, and Man-in-the-Middle."
        },
        {
          term: "asymmetric algorithm (Public Key)",
          definition: "A cipher that uses public and private keys. The keys are mathematically linked, using either Rivel, Shamir, Adleman (RSA) or elliptic curve cryptography (ECC) algorithms, but the private key is not derivable from the public one. An asymmetric key cannot reverse the operation it performs, so the public key cannot decrypt what it has encrypted, for example. Also known as Elliptic Curve Cryptography or ECC."
        },
        {
          term: "ATT&CK (Adversarial Tactics, Techniques, and Common Knowledge)",
          definition: "A knowledge base maintained by the MITRE Corporation for listing and explaining specific adversary tactics, techniques, and procedures."
        },
        {
          term: "attack surface",
          definition: "The points at which a network or application receives external connections or inputs/outputs that are potential vectors to be exploited by a threat actor."
        },
        {
          term: "attack vector",
          definition: "A specific path by which a threat actor gains unauthorized access to a system. Also known as vector."
        },
        {
          term: "authenticator",
          definition: "A PNAC switch or router that activates EAPoL and passes a supplicant's authentication data to an authenticating server, such as a RADIUS server."
        },
        {
          term: "automation",
          definition: "Using scripts and APIs to provision and deprovision systems without manual intervention."
        },
        {
          term: "Autopsy",
          definition: "The Sleuth Kit is an open source collection of command line and programming libraries for disk imaging and file analysis. Autopsy is a graphical frontend for these tools and also provides a case management/workflow tool. Also known as Sleuth Kit."
        },
        {
          term: "availability",
          definition: "The fundamental security goal of ensuring that computer systems operate continuously and that authorized persons can access data that they need."
        },
        {
          term: "BAS (building automation system)",
          definition: "Components and protocols that facilitate the centralized configuration and monitoring of mechanical and electrical systems within offices and data centers."
        },
        {
          term: "baseband radio",
          definition: "The chip and firmware in a smartphone that acts as a cellular modem."
        },
        {
          term: "baseline configuration",
          definition: "A collection of security and configuration settings that are to be applied to a particular system or network in the organization."
        },
        {
          term: "bash (Bourne again shell)",
          definition: "A command shell and scripting language for Unix-like systems. bastion host A server typically found in a DMZ that is configured to provide a single service to reduce the possibility of compromise."
        },
        {
          term: "behavioral analysis",
          definition: "A network monitoring system that detects changes in normal operating data sequences and identifies abnormal sequences. Also known as behavior-based detection."
        },
        {
          term: "BIA (business impact analysis)",
          definition: "A systematic activity that identifies organizational risks and determines their effect on ongoing, mission critical operations."
        },
        {
          term: "birthday attack",
          definition: "A type of password attack that exploits weaknesses in the mathematical algorithms used to encrypt passwords, in order to take advantage of the probability of different password inputs producing the same encrypted output."
        },
        {
          term: "block cipher",
          definition: "A type of symmetric encryption that encrypts data one block at a time, often in 64-bit blocks. It is usually more secure, but is also slower, than stream ciphers."
        },
        {
          term: "blockchain",
          definition: "A concept in which an expanding list of transactional records listed in a public ledger is secured using cryptography."
        },
        {
          term: "blue team",
          definition: "The defensive team in a penetration test or incident response exercise."
        },
        {
          term: "bluejacking",
          definition: "Sending an unsolicited message or picture message using a Bluetooth connection."
        },
        {
          term: "bluesnarfing",
          definition: "A wireless attack where an attacker gains access to unauthorized information on a device using a Bluetooth connection."
        },
        {
          term: "boot attestation",
          definition: "Report of boot state integrity data that is signed by a tamper-proof TPM key and reported to a network server."
        },
        {
          term: "botnet",
          definition: "A set of hosts that has been infected by a control program called a bot that enables attackers to exploit the hosts to mount attacks. Also known as zombie."
        },
        {
          term: "BPA (business partnership agreement)",
          definition: "Agreement by two companies to work together closely, such as the partner agreements that large IT companies set up with resellers and solution providers."
        },
        {
          term: "BPDU guard (Bridge Protocol Data Unit guard)",
          definition: "Switch port security feature that disables the port if it receives BPDU notifications related to spanning tree. This is configured on access ports where there any BPDU frames are likely to be malicious."
        },
        {
          term: "brute force attack",
          definition: "A type of password attack where an attacker uses an application to exhaustively try every possible alphanumeric combination to crack encrypted passwords."
        },
        {
          term: "buffer overflow",
          definition: "An attack in which data goes past the boundary of the destination buffer and begins to corrupt adjacent memory. This can allow the attacker to crash the system or execute arbitrary code."
        },
        {
          term: "bug bounty",
          definition: "Reward scheme operated by software and web services vendors for reporting vulnerabilities."
        },
        {
          term: "BYOD (bring your own device)",
          definition: "Security framework and tools to facilitate use of personally-owned devices to access corporate networks and data."
        }
      ]

    }
  },
  components: {
    LottieAnimation,
    Flashcard
  },
  computed: {
    currentTerm () {
      return this.terms[this.currentTermIndex]
    }
  },
  methods: {
    keyPressed(e) {
      if (this.keyboardShortcuts === true) {
        console.log(e)
      }
    },
    alertInfo (title, msg) {
      this.$vs.notify({
        color: 'primary',
        title: title,
        text: msg,
      })
    },
    cardTransition () {
      if(this.cardAnimationActive === true) return false;
      this.cardAnimationActive = true
      this.showCard = false
      setTimeout(() => {this.showCard = true; this.cardAnimationActive = false}, 300)
    },
    nextTerm() {
      this.cardTransition()
      if (this.currentTermIndex + 1 > this.terms.length) return this.alertInfo('Set Finished', '');
      return this.currentTermIndex++
    },
    previousTerm() {
      this.cardTransition()
      if (this.currentTermIndex - 1 < 0) return this.alertInfo('Set Start', '');
      return this.currentTermIndex--
    }
  },
  mounted() {
    this.cardTransition()
    window.addEventListener('keypress', this.keyPressed);
  },
  beforeDestroy() {
    window.addEventListener('keypress', this.keyPressed);
  }
}
</script>
<style lang="scss">
#LearnRenderer {
    cursor: url('~@/assets/images/cursor/cursor-auto.png'), auto;

    .cursor-pointer-fc {
      cursor: url('~@/assets/images/cursor/cursor-drag.png'), auto;
    }

  //  background: #050035;
  .LearnProgressBar {
    background-color: rgba(0, 0, 0, 0.30);
    z-index: 999999;
    max-width: 950px;
  }

  .FcAppContainer {
    // padding-top: 10%;
    padding-bottom: 7%;
    max-width: 850px;
    width: 70%;
    font-size: 32px;
    font-weight: 600;
    align-items: center;
    justify-content: center;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;
    display: flex;
  }

  .bottomActionBar {
    min-height: 13.5%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: solid rgba(229, 229, 229, 0.25);
    border-width: 2px 0 0 0;

    &.answerDisplay {
      border-color: transparent;
      background: rgba(0, 0, 0, 0.3);
    }
  }

  .actionButtons {
    max-width: 1100px;
    width: 100%;
  }

  .nextButton {
    font-weight: 600;
    font-size: 2rem;
    padding: 10px 40px 10px 40px;
    border-radius: 20px;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:disabled {
      background: #afafaf !important;
    }
  }

  .skipButton {
    font-weight: 600;
    font-size: 2rem;
    padding: 10px 40px 10px 40px;
    border: 2px solid rgba(229, 229, 229, 0.25) !important;
    color: #FFF !important;
    border-radius: 20px;
    -moz-user-select: none;
    -khtml-user-select: none;
    -webkit-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &:disabled {
      opacity: 0%;
    }
  }

  .fcLeftArrow {
    //position: absolute;
    //left: 25%;
    z-index: 99;
  }

  .fcRightArrow {
    //position: absolute;
    //right: 25%;
    z-index: 99;
  }

  .fcCard {
    width: 40rem;
    height: 25rem;
    margin-left: 7.5%;
    margin-right: 7.5%;
  }

}


</style>
